import React, { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Share2, Link as LinkIcon, Check, Mail, MessageSquare, X } from "lucide-react";
import { trackClick } from "../lib/analytics";

/**
 * Build the URL we hand to native share / put on the clipboard.
 *
 *  We route through backend OG endpoints so social crawlers (WhatsApp,
 *  Telegram, Facebook, X, LinkedIn, Slack, Discord) ALWAYS see a rich
 *  preview:
 *    - "/"                       → /api/og/site            (brand card)
 *    - "/listing/:id"            → /api/og/listing/:id     (ad card)
 *    - "/digital-stores/:slug"   → /api/og/digital-store/:slug
 *
 *  Humans get transparently redirected to the SPA route via meta-refresh.
 *  Anything else gets the canonical adukoo URL — no rich preview but a
 *  working link.
 */
function buildShareUrl(path) {
    if (typeof window === "undefined") return path;
    const origin = window.location.origin;
    // Cache-bust + per-share uniqueness.
    //
    // We carry two query params:
    //   • `v=<og-mtime-base36>` — flips when the admin uploads a new OG
    //     image, so LinkedIn / Facebook / X / Pinterest treat each fresh
    //     image as a new URL and re-scrape immediately.
    //   • `t=<random-base36>` — a tiny per-render random token so EVERY
    //     individual share is unique. WhatsApp caches OG previews keyed
    //     by exact URL; if it ever caches a broken preview for a URL
    //     (race condition between the og:image being uploaded and a
    //     crawler fetching the page), every subsequent share of that
    //     same URL sticks to the broken cached preview until the user
    //     manually edits the URL. Adding `t=` per share dodges this:
    //     WhatsApp sees a brand-new URL on every share, no cached
    //     broken entries possible.
    const v = (typeof window !== "undefined" && window.__adukoo_og_v) || "";
    // Short base36 random token (8 chars ≈ 2.8 × 10¹² possibilities — no
    // realistic chance of collision between two consecutive shares).
    const t = Math.floor(Math.random() * 36 ** 8).toString(36).padStart(8, "0");
    const qs = [v && `v=${v}`, `t=${t}`].filter(Boolean).join("&");
    const sep = qs ? "?" : "";
    if (!path || path === "/" || path === "") {
        return `${origin}/${sep}${qs}`;
    }
    // For listings/stores we still route through the dynamic API because
    // those entities don't have a static og:image — the server has to
    // assemble one from DB content.
    const mListing = path.match(/^\/listing\/(.+)$/);
    if (mListing) return `${origin}/api/og/listing/${encodeURIComponent(mListing[1])}${sep}${qs}`;
    const mStore = path.match(/^\/digital-stores\/([^/?#]+)$/);
    if (mStore) return `${origin}/api/og/digital-store/${encodeURIComponent(mStore[1])}${sep}${qs}`;
    return `${origin}${path}${sep}${qs}`;
}
function buildHumanUrl(path) {
    if (typeof window === "undefined") return path;
    return `${window.location.origin}${path}`;
}

/* NOTE — we used to attach the listing image as a File via Web Share API
 * Level 2. That actually breaks rich link previews on WhatsApp / Telegram:
 * those apps treat the share as a photo + caption instead of a URL, so the
 * recipient sees only the picture (no title, no price, no description).
 *
 * Sharing just {title, text, url} lets each platform fetch our backend
 * `/api/og/listing/{id}` endpoint and render a proper rich preview with
 * title + description + price + image — which is exactly what users want.
 */

/* ------------ minimalist colour-correct SVG icons ----------- */
const Icon = {
    WhatsApp: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M20.5 3.5A11 11 0 0 0 3.3 17.2L2 22l4.9-1.3a11 11 0 0 0 16.6-9.3 11 11 0 0 0-3-7.9ZM12 20a8 8 0 0 1-4.1-1.1l-.3-.2-2.9.8.8-2.8-.2-.3A8 8 0 1 1 20 12a8 8 0 0 1-8 8Zm4.6-6c-.3-.1-1.6-.8-1.8-.9-.3-.1-.5-.1-.6.1l-.9 1c-.2.2-.3.2-.6.1a6.6 6.6 0 0 1-3.2-2.8c-.3-.4 0-.4.2-.6l.4-.5.2-.4c.1-.2 0-.3 0-.5l-.7-1.7c-.2-.5-.4-.4-.6-.4H8.4a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.8 11 11 0 0 0 4.3 3.8c2.6 1 2.6.7 3.1.6a2.7 2.7 0 0 0 1.8-1.3 2.2 2.2 0 0 0 .2-1.3c-.1-.1-.3-.2-.6-.3Z" /></svg>,
    Telegram: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M22 3 2 10.9l5.5 1.7L18 6.5l-8.6 8 .4 6 3.4-3.4 4.4 3.3c.8.6 1.4.3 1.6-.8L22 3Z" /></svg>,
    X: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M18.3 3H21l-6.5 7.4L22 21h-6l-4.7-6.1L5.8 21H3l7-8-6.7-10h6.2l4.2 5.6L18.3 3Zm-1 16.2h1.6L7.8 4.7H6.1l11.2 14.5Z" /></svg>,
    Facebook: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M22 12a10 10 0 1 0-11.6 9.9v-7H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.5 2.9h-2.3v7A10 10 0 0 0 22 12Z" /></svg>,
    LinkedIn: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2ZM8.3 18.3H5.7V9.7h2.6v8.6Zm-1.3-9.8a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3Zm11.3 9.8h-2.6v-4.2c0-1 0-2.3-1.4-2.3s-1.6 1.1-1.6 2.2v4.3h-2.6V9.7h2.5v1.2h.1a2.7 2.7 0 0 1 2.5-1.4c2.7 0 3.2 1.8 3.2 4.1v4.7Z" /></svg>,
    Reddit: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M22 12c0-1.1-.9-2-2-2-.6 0-1.1.2-1.5.6-1.5-1-3.5-1.7-5.7-1.8l1.2-3.7 3.2.7c0 .9.7 1.6 1.6 1.6s1.6-.7 1.6-1.6S19.7 4.2 18.8 4.2c-.6 0-1.2.4-1.5.9l-3.6-.8c-.2 0-.4.1-.5.3l-1.4 4.2C9.4 8.9 7.4 9.6 6 10.6c-.4-.4-.9-.6-1.5-.6-1.1 0-2 .9-2 2 0 .8.5 1.5 1.2 1.8 0 .2-.1.4-.1.6C3.6 17.5 7.4 20 12 20s8.4-2.5 8.4-5.6c0-.2 0-.4-.1-.6.7-.3 1.2-1 1.2-1.8Zm-13.6 1.5c0-.8.7-1.4 1.5-1.4s1.4.6 1.4 1.4-.7 1.5-1.4 1.5-1.5-.6-1.5-1.5Zm6.7 3c-.7.7-2.1.7-2.5.7-.5 0-1.8 0-2.5-.7-.2-.2-.2-.4 0-.6.2-.2.4-.2.6 0 .4.4 1.4.5 1.9.5s1.5-.1 1.9-.5c.2-.2.4-.2.6 0 .2.2.2.4 0 .6Zm-.2-1.5c-.8 0-1.4-.7-1.4-1.5s.6-1.4 1.4-1.4c.8 0 1.5.6 1.5 1.4 0 .9-.7 1.5-1.5 1.5Z" /></svg>,
    Pinterest: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M12 2a10 10 0 0 0-3.6 19.3c-.1-.8-.2-2 0-2.9.2-.7 1.1-4.4 1.1-4.4s-.3-.5-.3-1.3c0-1.3.7-2.2 1.7-2.2.8 0 1.2.6 1.2 1.3 0 .8-.5 2-.8 3.1-.2.9.5 1.7 1.4 1.7 1.7 0 3-1.8 3-4.4 0-2.3-1.7-3.9-4.1-3.9-2.8 0-4.4 2.1-4.4 4.2 0 .8.3 1.7.7 2.2.1.1.1.2.1.2l-.3 1.1c0 .2-.2.3-.3.2-1.3-.6-2.1-2.5-2.1-4 0-3.2 2.4-6.2 6.8-6.2 3.6 0 6.3 2.5 6.3 5.9 0 3.5-2.2 6.4-5.4 6.4-1 0-2-.5-2.4-1.2l-.6 2.5c-.2.9-.9 2-1.3 2.7A10 10 0 1 0 12 2Z" /></svg>,
    // Google Mail (Gmail compose) — different from generic `mailto:`. The
    // generic mailto handler often opens Outlook on Windows or no client
    // at all in browsers; Gmail's compose URL works from any browser.
    Gmail: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M22 6.2v11.6a1.4 1.4 0 0 1-1.4 1.4h-2.8V10.6L12 14.4l-5.8-3.8v8.6H3.4A1.4 1.4 0 0 1 2 17.8V6.2c0-.5.3-1 .8-1.2L4.2 4l7.8 5.1L19.8 4l1.4 1c.5.2.8.7.8 1.2Z" /></svg>,
    // Snapchat — yellow ghost mark.
    Snapchat: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M12 2a5.6 5.6 0 0 1 5.6 5.4c0 1.3-.1 2.1-.2 2.6.1 0 .3.1.6.2.3.2.6.4.6.7 0 .5-.9 1-1.8 1.5l-.5.3a3.6 3.6 0 0 1-.6 1l1.1.4c.7.2 1.4.5 1.8.8.3.2.4.5.4.7 0 .4-.4.7-1 .9l-.7.2c-.1.2-.2.6-.3.8-.1.2-.3.3-.6.3h-.2c-.4 0-.7-.1-1.1-.2h-.6c-.4 0-.7.1-1.1.3-.6.4-1.4.8-2.4.8h-.4c-1 0-1.8-.4-2.4-.8-.4-.2-.7-.3-1.1-.3h-.6c-.4.1-.7.2-1.1.2-.3 0-.5-.1-.6-.3l-.3-.8-.7-.2c-.6-.2-1-.5-1-.9 0-.2.1-.5.4-.7.4-.3 1.1-.6 1.8-.8l1.1-.4a3.6 3.6 0 0 1-.6-1l-.5-.3c-.9-.5-1.8-1-1.8-1.5 0-.3.3-.5.6-.7.3-.1.5-.2.6-.2-.1-.5-.2-1.3-.2-2.6A5.6 5.6 0 0 1 12 2Z" /></svg>,
    // Threads (Meta) — three concentric arcs.
    Threads: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M16.7 10.9c-.1 0-.2-.1-.3-.2-.4-2.2-1.7-3.4-3.8-3.6-1.2-.1-2.3.4-3 1.4l-1.4-1c1.1-1.5 2.8-2.3 4.6-2.1 2.7.2 4.6 1.9 5.3 4.6 2.1.2 3.4 1.5 3.4 3.5 0 1.5-.7 2.7-2 3.5-1.3.8-3 .9-4.7.3-2.4-.8-3.5-3.3-3.5-5.5 0-1.8.8-3.2 2.3-3.8 1-.4 2.1-.4 3 .1.4.2.7.5.9.9l-1.6 1c-.4-.5-.9-.7-1.5-.5-.8.3-1.4 1.2-1.4 2.3 0 1.9 1.1 3.4 2.5 3.9 1 .4 2.1.3 2.9-.2.6-.4 1-1 1-1.7 0-1.3-1.2-1.8-2.7-1.9Z" /></svg>,
    // Skype — single sweeping S, simplified.
    Skype: (p) => <svg viewBox="0 0 24 24" {...p} fill="currentColor"><path d="M21.4 14.5a9.5 9.5 0 0 1-11 7.3 5 5 0 0 1-6.4-5.4 9.5 9.5 0 0 1 6.8-11.5 5 5 0 0 1 6.4 5.4 9.5 9.5 0 0 1 4.2 4.2Zm-9.4 3.5c2.4 0 4-1.3 4-3.2 0-1.5-1-2.5-3-3l-1.6-.4c-.7-.1-1-.4-1-.8 0-.5.5-.9 1.3-.9.8 0 1.3.3 1.5.9.2.4.5.6.9.6.5 0 .9-.4.9-.9 0-1.2-1.4-2.2-3.3-2.2-2.1 0-3.5 1.2-3.5 2.8 0 1.4 1 2.3 2.8 2.7l1.6.4c.8.2 1.2.5 1.2 1 0 .6-.6 1-1.6 1s-1.5-.3-1.8-1c-.2-.4-.5-.6-.9-.6-.5 0-.9.4-.9.9 0 1.2 1.6 2.4 3.4 2.4Z" /></svg>,
    Email: Mail,
    SMS: MessageSquare,
    Copy: LinkIcon,
};

/**
 * Universal Adukoo share button.
 *
 * Strategy:
 *  1. On Capacitor native (Android / iOS app) → use @capacitor/share which
 *     opens the proper OS share sheet — no custom UI needed.
 *  2. On any browser with `navigator.share` (most mobile browsers) → use it
 *     directly. We attach the listing image as a File when possible.
 *  3. On desktop / legacy browsers without native share → show a compact
 *     icon-grid modal (3 columns) that's much tighter than the old long
 *     vertical list and renders consistently everywhere.
 */
export default function ShareButton({ path, title, price, variant = "lg", listingId }) {
    const [sheetOpen, setSheetOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [ogVersion, setOgVersion] = useState(
        typeof window !== "undefined" ? window.__adukoo_og_v || "" : "",
    );

    // v78.26 — De-duplicate the /api/og/site/version request across
    // every ShareButton instance on the page. Previously each product
    // card mounted its own ShareButton and fired its own fetch, so a
    // single home-page render kicked off 70+ identical requests on
    // Safari (which doesn't dedupe via the HTTP cache for `credentials:
    // "omit"` GETs). We now share ONE in-flight promise stashed on
    // `window.__adukoo_og_v_promise` — the first caller starts the
    // fetch, all subsequent callers await the same promise. After it
    // resolves the value is cached on `window.__adukoo_og_v` and the
    // promise is cleared so future cold renders can refresh it.
    React.useEffect(() => {
        if (ogVersion) return;
        let cancelled = false;
        const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
        if (typeof window === "undefined") return;
        if (!window.__adukoo_og_v_promise) {
            window.__adukoo_og_v_promise = (async () => {
                try {
                    const r = await fetch(`${BACKEND}/api/og/site/version`, { credentials: "omit" });
                    if (!r.ok) return "";
                    const j = await r.json();
                    const v = j?.version ? String(j.version) : "";
                    if (v) window.__adukoo_og_v = v;
                    return v;
                } catch { return ""; }
            })();
        }
        window.__adukoo_og_v_promise.then((v) => {
            if (!cancelled && v) setOgVersion(v);
        });
        return () => { cancelled = true; };
    }, [ogVersion]);

    // Re-derive on every render so `?v=` reflects the latest known version
    // (including the version-state effect on first mount).
    const url = buildShareUrl(path);
    const humanUrl = buildHumanUrl(path);
    // We deliberately do NOT include a text message in the share. WhatsApp /
    // Telegram / Facebook already render a rich Open-Graph card from the URL
    // alone — title, image, price, description all show. Sending an extra
    // `text` field made WhatsApp print "Adukoo™ — The World's Marketplace on
    // Adukoo <url>" below the preview card, which is redundant and noisy.

    const copyLink = async () => {
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(humanUrl);
            } else {
                const ta = document.createElement("textarea");
                ta.value = humanUrl; document.body.appendChild(ta); ta.select();
                document.execCommand("copy"); document.body.removeChild(ta);
            }
            setCopied(true);
            toast.success("Link copied");
            setTimeout(() => setCopied(false), 1500);
            return true;
        } catch {
            toast.error("Could not copy");
            return false;
        }
    };

    /**
     * Open the native OS share sheet via Capacitor (preferred on mobile app)
     * or `navigator.share` (preferred on mobile browser). Returns true if
     * the native UI handled the share — caller then skips the desktop sheet.
     *
     * IMPORTANT: on desktop (macOS/Windows/Linux) `navigator.share` is now
     * available in Chrome/Edge/Safari but the OS share sheet there has
     * NO social-media targets — it only offers Mail/Notes/AirDrop. Users
     * expect WhatsApp/Telegram/Facebook/etc. so on non-touch devices we
     * skip the native sheet and always show our custom icon grid.
     */
    const openNative = async () => {
        // Capacitor app — use the proper plugin if available
        try {
            const cap = await import("@capacitor/core").catch(() => null);
            if (cap?.Capacitor?.isNativePlatform?.()) {
                const { Share } = await import("@capacitor/share").catch(() => ({}));
                if (Share?.share) {
                    // Only URL — let the receiving app render a rich preview
                    // from our OG endpoint instead of duplicating the title.
                    await Share.share({ url, dialogTitle: "Share on Adukoo" });
                    if (listingId) trackClick(listingId, "share");
                    return true;
                }
            }
        } catch { /* not Capacitor — fall through */ }

        // Web Share API — only when this device's PRIMARY pointer is
        // coarse (phone/tablet/touchscreen kiosk). Desktop Chrome/Safari
        // now also ship `navigator.share` but the OS share sheet on
        // macOS/Windows has NO social-media targets — only Mail, Notes,
        // AirDrop, etc. Showing it on a laptop is exactly what users
        // complain about ("no WhatsApp option on Mac"). `pointer: coarse`
        // is the standards-track way to identify touch-first devices and
        // is true on iOS Safari + Android Chrome, false on macOS / desktop
        // browsers regardless of whether the OS exposes navigator.share.
        const isTouchPrimary = typeof window !== "undefined"
            && typeof window.matchMedia === "function"
            && window.matchMedia("(pointer: coarse)").matches;
        if (isTouchPrimary && typeof navigator !== "undefined" && navigator.share) {
            try {
                // Share ONLY the URL — receiving app fetches our OG meta.
                await navigator.share({ url });
                if (listingId) trackClick(listingId, "share");
                return true;
            } catch {
                /* user cancelled or unsupported — fall through */
            }
        }
        return false;
    };

    const onPrimaryClick = (e) => {
        // Synchronously block parent-link nav + bubbling. Do NOT await
        // anything here — earlier versions did `await openNative()` first
        // which created a perceptible flicker on desktop: the click hit
        // the underlying ProductCard `<Link>` (hover transition fired,
        // tile briefly highlighted) BEFORE the share sheet opened.
        e?.preventDefault?.();
        e?.stopPropagation?.();

        // Desktop / non-touch primary: open the centred modal immediately.
        // No native share sheet on macOS / Windows / Linux has WhatsApp
        // etc., so the desktop modal is always the right answer.
        const isTouchPrimary = typeof window !== "undefined"
            && typeof window.matchMedia === "function"
            && window.matchMedia("(pointer: coarse)").matches;
        if (!isTouchPrimary) {
            setSheetOpen(true);
            return;
        }

        // Touch-primary: try native share / Capacitor first; if it
        // declines (user cancelled, or no plugin), fall back to the modal.
        (async () => {
            const handled = await openNative();
            if (!handled) setSheetOpen(true);
        })();
    };

    // v79.121 — Stop ALL pointer / mouse / touch events from bubbling
    // to the parent card so tapping Share NEVER also opens the ad
    // page beneath it. Founder bug report: "when i click on share
    // button on any page link gets copied and ad page also open."
    const stopPointer = (e) => {
        e?.stopPropagation?.();
        // We also swallow the parent link's default click on mobile
        // where browsers may synthesise a click from pointerdown.
        if (e?.type === "mousedown" || e?.type === "pointerdown" || e?.type === "touchstart") {
            e?.preventDefault?.();
        }
    };

    let triggerClass;
    if (variant === "lg") triggerClass = "w-full h-11 rounded-full flex items-center justify-center gap-2 bg-aduku-yellow text-aduku-navy hover:bg-aduku-yellow-600 font-semibold";
    else if (variant === "sm-labelled") triggerClass = "h-10 px-4 rounded-full flex items-center gap-2 bg-aduku-yellow text-aduku-navy hover:bg-aduku-yellow-600 font-semibold text-sm";
    else if (variant === "cta") triggerClass = "h-10 sm:h-11 px-5 sm:px-6 rounded-full flex items-center gap-2 bg-aduku-yellow text-aduku-navy hover:bg-aduku-yellow-600 font-bold text-sm";
    else if (variant === "sm-icon") triggerClass = "w-8 h-8 rounded-full flex items-center justify-center shadow-md backdrop-blur-md bg-white/80 text-aduku-navy hover:bg-white transition-colors";
    // v78.9 — Reels variant: stacked icon-with-label tile that matches
    // the Reels right rail (SubscribeButton uses the same layout).
    else if (variant === "reels") triggerClass = "flex flex-col items-center justify-center gap-1 w-12 active:scale-90 transition-transform text-white";
    else triggerClass = "w-8 h-8 rounded-full flex items-center justify-center shadow-md backdrop-blur-md bg-white/80 text-aduku-navy hover:bg-white transition-colors";

    const ENC = encodeURIComponent;
    const encUrl = ENC(url);

    // Deep-link share targets only receive the URL. Each platform (WhatsApp,
    // Telegram, X, FB, LinkedIn, Reddit, Pinterest) renders the rich OG
    // preview itself, so we don't pre-pend our own message — that was
    // producing redundant "Adukoo … <url>" lines on WhatsApp.
    const DESKTOP_TARGETS = [
        { id: "wa", Icon: Icon.WhatsApp, label: "WhatsApp", href: `https://wa.me/?text=${encUrl}`, bg: "bg-[#25D366] text-white" },
        { id: "tg", Icon: Icon.Telegram, label: "Telegram", href: `https://t.me/share/url?url=${encUrl}`, bg: "bg-[#229ED9] text-white" },
        { id: "x", Icon: Icon.X, label: "X / Twitter", href: `https://twitter.com/intent/tweet?url=${encUrl}`, bg: "bg-black text-white" },
        { id: "fb", Icon: Icon.Facebook, label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}`, bg: "bg-[#1877F2] text-white" },
        { id: "li", Icon: Icon.LinkedIn, label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`, bg: "bg-[#0A66C2] text-white" },
        { id: "rd", Icon: Icon.Reddit, label: "Reddit", href: `https://www.reddit.com/submit?url=${encUrl}&title=${ENC(title || "Adukoo")}`, bg: "bg-[#FF4500] text-white" },
        { id: "pn", Icon: Icon.Pinterest, label: "Pinterest", href: `https://pinterest.com/pin/create/button/?url=${encUrl}`, bg: "bg-[#E60023] text-white" },
        // Gmail compose URL works in any browser (mailto: often opens Outlook
        // or no client at all). Subject = listing title, body = the URL.
        { id: "gmail", Icon: Icon.Gmail, label: "Gmail", href: `https://mail.google.com/mail/?view=cm&fs=1&su=${ENC(title || "Adukoo")}&body=${encUrl}`, bg: "bg-[#EA4335] text-white" },
        // Threads (Meta) intent URL. Falls back to threads.net web on
        // desktop, deep-links into the Threads app on mobile.
        { id: "threads", Icon: Icon.Threads, label: "Threads", href: `https://www.threads.net/intent/post?text=${ENC((title || "Adukoo") + " — " + url)}`, bg: "bg-black text-white" },
        // Snapchat — official "Creative Kit" share URL. Mobile-app deep
        // link first; if the user is on desktop Snapchat will direct them
        // to scan a QR. Works for the screenshot-style listings the
        // founder showed in the Ford Figo sample.
        { id: "snap", Icon: Icon.Snapchat, label: "Snapchat", href: `https://www.snapchat.com/scan?attachmentUrl=${encUrl}`, bg: "bg-[#FFFC00] text-aduku-navy" },
        // Skype — used heavily by founders' B2B / agency contacts who
        // negotiate Adukoo store partnerships via Skype call.
        { id: "skype", Icon: Icon.Skype, label: "Skype", href: `https://web.skype.com/share?url=${encUrl}&text=${ENC(title || "Adukoo")}`, bg: "bg-[#00AFF0] text-white" },
        { id: "em", Icon: Icon.Email, label: "Email", href: `mailto:?subject=${ENC(title || "Adukoo")}&body=${encUrl}`, bg: "bg-aduku-bg text-aduku-navy" },
        { id: "sms", Icon: Icon.SMS, label: "SMS", href: `sms:?body=${encUrl}`, bg: "bg-aduku-bg text-aduku-navy" },
    ];

    const onTargetClick = (e, t) => {
        if (listingId) trackClick(listingId, "share");
        // Close the sheet IMMEDIATELY so when the user returns to the app
        // (after sharing via WhatsApp / Telegram / LinkedIn / etc.) they
        // see the home page, not the leftover share grid. The default
        // link nav still fires because we don't preventDefault.
        setSheetOpen(false);
    };

    // v79.107 — Sync the share sheet with browser history so tapping
    // Back (browser button, Android system back, WhatsApp "return to
    // app") CLOSES the sheet instead of navigating away from the page.
    //
    // Contract:
    //  • When the sheet opens, push a marker state entry.
    //  • When the marker is popped (Back / Android system-back),
    //    close the sheet locally.
    //  • When the user closes the sheet via X / backdrop / picking a
    //    target, we call history.back() to remove the marker so we
    //    don't leave a dead entry that would swallow a subsequent
    //    Back press.
    React.useEffect(() => {
        if (!sheetOpen) return;
        const marker = { adukooShareSheet: Date.now() };
        try { window.history.pushState(marker, ""); } catch { /* ignore */ }
        const onPop = () => setSheetOpen(false);
        window.addEventListener("popstate", onPop);
        return () => {
            window.removeEventListener("popstate", onPop);
            // If the sheet closed for any reason OTHER than a popstate
            // (X click, target click, unmount), remove our history
            // entry so Back doesn't hit a dead marker.
            const cur = window.history.state;
            if (cur && cur.adukooShareSheet === marker.adukooShareSheet) {
                try { window.history.back(); } catch { /* ignore */ }
            }
        };
    }, [sheetOpen]);

    // Belt-and-braces: also close the sheet when the page becomes hidden
    // (e.g., user picked a target, was deep-linked into WhatsApp, then
    // returned). Some mobile browsers don't dispatch a fresh click on the
    // back-nav, so `setSheetOpen(false)` in onTargetClick alone isn't
    // enough — visibilitychange catches the return trip too.
    React.useEffect(() => {
        if (!sheetOpen) return;
        const onHide = () => {
            if (document.visibilityState === "hidden") setSheetOpen(false);
        };
        document.addEventListener("visibilitychange", onHide);
        return () => document.removeEventListener("visibilitychange", onHide);
    }, [sheetOpen]);

    return (
        <>
            <button
                type="button"
                onClick={onPrimaryClick}
                onPointerDown={stopPointer}
                onMouseDown={stopPointer}
                onTouchStart={stopPointer}
                data-testid="share-button"
                className={triggerClass}
                aria-label="Share"
                title="Share"
            >
                {variant === "sm" || variant === "sm-icon" ? (
                    <Share2 className="w-4 h-4" />
                ) : variant === "reels" ? (
                    <>
                        <span className="w-11 h-11 rounded-full flex items-center justify-center bg-white/15 backdrop-blur">
                            <Share2 className="w-5 h-5" strokeWidth={2.4} />
                        </span>
                        <span className="text-[10px] font-bold leading-none">Share</span>
                    </>
                ) : (
                    <>
                        <Share2 className="w-4 h-4" />
                        <span>{copied ? "Link copied" : "Share"}</span>
                    </>
                )}
            </button>

            {/* Desktop / no-native-share fallback — compact icon-grid sheet.
                Rendered via a Portal to <body> because any ancestor with a
                `transform` (e.g. `.aduku-card-lift:hover { translateY(-3px) }`)
                or `filter` / `backdrop-filter` / `will-change` becomes the
                containing block for `position: fixed`, clipping this sheet
                inside the ProductCard. That was the "share dropdown covers
                tiles / flickers" bug reported by founders.  */}
            {sheetOpen && typeof document !== "undefined" && createPortal((
                <div
                    role="dialog"
                    aria-modal="true"
                    data-testid="share-sheet"
                    className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-sm"
                    onClick={(e) => { if (e.target === e.currentTarget) setSheetOpen(false); }}
                >
                    <div
                        className="w-full sm:w-[420px] bg-white rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 shadow-2xl"
                        style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 20px)" }}
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="font-display font-black text-aduku-navy text-base">Share via</div>
                            <button
                                onClick={() => setSheetOpen(false)}
                                data-testid="share-sheet-close"
                                aria-label="Close share menu"
                                className="w-8 h-8 rounded-full bg-aduku-bg hover:bg-aduku-bg/70 text-aduku-navy flex items-center justify-center"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                            {DESKTOP_TARGETS.map((t) => (
                                <a
                                    key={t.id}
                                    href={t.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`share-target-${t.id}`}
                                    onClick={(e) => onTargetClick(e, t)}
                                    className="flex flex-col items-center gap-1.5 group"
                                >
                                    <div className={`w-12 h-12 rounded-2xl ${t.bg} flex items-center justify-center group-hover:scale-105 transition-transform shadow-sm`}>
                                        <t.Icon className="w-5 h-5" />
                                    </div>
                                    <span className="text-[11px] text-aduku-navy/80 font-semibold text-center leading-tight">{t.label}</span>
                                </a>
                            ))}
                            <button
                                onClick={copyLink}
                                data-testid="share-target-copy"
                                className="flex flex-col items-center gap-1.5 group"
                            >
                                <div className="w-12 h-12 rounded-2xl bg-aduku-navy text-aduku-yellow flex items-center justify-center group-hover:scale-105 transition-transform shadow-sm">
                                    {copied ? <Check className="w-5 h-5" /> : <Icon.Copy className="w-5 h-5" />}
                                </div>
                                <span className="text-[11px] text-aduku-navy/80 font-semibold text-center leading-tight">{copied ? "Copied" : "Copy link"}</span>
                            </button>
                        </div>

                        <div className="mt-4 pt-3 border-t border-aduku-border flex items-center justify-between gap-3">
                            <div className="text-[11px] text-aduku-navy/60 truncate">{humanUrl}</div>
                            <button
                                onClick={copyLink}
                                data-testid="share-copy-inline"
                                className="shrink-0 h-9 px-3 rounded-full bg-aduku-bg hover:bg-aduku-yellow/30 text-aduku-navy text-xs font-bold"
                            >
                                {copied ? "Copied!" : "Copy"}
                            </button>
                        </div>
                    </div>
                </div>
            ), document.body)}
        </>
    );
}
